import { cn } from "cn";
import { useEffect, useState } from "react";
import {
  type AttachmentFile,
  AttachmentTypeIcon,
  resolveAttachmentPreviewKind,
} from "@/components/ui/attachment-new";
import { Button } from "@/components/ui/button/button";
import { CircularProgressbar } from "@/components/ui/circular-progressbar";
import { BwCloseLine } from "@/components/ui/icons";
import { getBytes } from "@/libs/file-helper";
import type {
  AttachmentSource,
  AttachmentUploadStatus,
} from "@/libs/form-attachment";

export type ListItemAttachmentProps = {
  attachment: AttachmentSource;
  onDelete?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  status?: AttachmentUploadStatus;
  percent?: number;
};

export default function ListItemAttachment({
  attachment,
  onDelete,
  className,
  status,
  percent,
}: ListItemAttachmentProps) {
  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onDelete) {
      onDelete(e);
    }
  };
  // An absent `status` keeps the pre-migration behaviour: treated as in-flight.
  const isUploaded = status === "done";
  const originalContentUri =
    attachment.originalContentUri ?? attachment.contentUri ?? "";
  const fileName = attachment.fileName ?? "";
  return (
    <div
      className={cn(
        /* sorted */
        "flex",
        "items-center gap-3",
        "px-4 py-2",
        "rounded-lg border border-border-dark-low",
        status === "error" && "border-border-negative-max",
        className,
      )}
    >
      {!isUploaded && (
        <div className="flex flex-col items-center justify-center w-6 h-6 shrink-0">
          <CircularProgressbar
            value={percent || 0}
            className="w-6 h-6 text-text-primary"
          />
        </div>
      )}
      {isUploaded && originalContentUri && (
        <ListItemAttachmentThumbnail
          fileName={fileName}
          originalContentUri={originalContentUri}
          thumbnailUri={attachment.thumbnailUri}
          contentType={attachment.contentType}
        />
      )}
      <div className="grow">
        <p
          className="text-sm leading-5 font-normal text-text-dark-max line-clamp-1 break-all"
          title={fileName}
        >
          {fileName}
        </p>
        <p className="text-sm leading-5 font-normal text-text-dark-high">
          {getBytes(attachment.fileSize ?? 0)}
        </p>
      </div>
      {onDelete && (
        <Button
          kind="ghost"
          variant="neutral"
          size="icon-md"
          className="shrink-0"
          onClick={handleDelete}
        >
          <BwCloseLine size={24} />
        </Button>
      )}
    </div>
  );
}

/**
 * The row's 32px mark: a photo (or a video's poster) when there is one to paint, the
 * file's type icon otherwise — and when the browser cannot decode the picture.
 */
function ListItemAttachmentThumbnail(file: AttachmentFile) {
  const preview = resolveAttachmentPreviewKind(file);
  const src = preview.kind === "picture" ? preview.src : null;
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={file.fileName}
        loading="lazy"
        decoding="async"
        draggable={false}
        className={cn(
          /* sorted */
          "shrink-0",
          "size-8",
          "border border-border-dark-low rounded-lg object-cover",
        )}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <AttachmentTypeIcon
      fileName={file.fileName}
      contentType={file.contentType}
      className="size-8 shrink-0"
    />
  );
}
